export type ToothNotation = 'fdi' | 'haderup';

export type ToothStatusType =
  | 'healthy'
  | 'caries'
  | 'filled'
  | 'crown'
  | 'missing'
  | 'implant'
  | 'root_canal'
  | 'bridge'
  | 'extraction_planned'
  | 'fracture'
  | 'periapical_abscess'
  | 'veneer'
  | 'post_and_core';

// Which statuses are valid for the DB p_status TEXT column
export const DB_VALID_STATUSES: ToothStatusType[] = [
  'healthy', 'caries', 'filled', 'crown', 'missing',
  'implant', 'root_canal', 'bridge', 'extraction_planned',
];

export type ToothType = 'permanent' | 'primary' | 'implant' | 'missing' | 'unerupted';

export type Surface = 'mesial' | 'distal' | 'buccal' | 'lingual' | 'occlusal';

export type RootZone = 'cervical' | 'mid' | 'apex';

export type RootCondition =
  | 'healthy'
  | 'root_canal_treated'
  | 'infected'
  | 'calcification'
  | 'resorption'
  | 'fracture'
  | 'perforation'
  | 'post';

export interface SurfaceStatus {
  conditions: ToothStatusType[];
  material?: string;
}

export interface RootZoneStatus {
  zone: RootZone;
  conditions: RootCondition[];
}

export interface ToothData {
  id: string;
  tooth_fdi: string;
  tooth_type: ToothType;
  status: ToothStatusType[];
  surfaces: Partial<Record<Surface, SurfaceStatus | { status?: string; material?: string }>>;
  root_zones?: RootZoneStatus[];
  notes?: string;
  last_examined_at?: string;
}

export interface ToothChartProps {
  teeth: ToothData[];
  selectedTeeth: string[];
  onToothSelect: (fdi: string) => void;
  onToothMultiSelect?: (fdi: string[]) => void;
  notation?: ToothNotation;
  readOnly?: boolean;
  className?: string;
}

/** Normalize either old {status: string} or new {conditions: string[]} format */
export function getSurfaceConditions(
  surface: SurfaceStatus | { status?: string; material?: string } | undefined,
): ToothStatusType[] {
  if (!surface) return [];
  if ('conditions' in surface) return (surface as SurfaceStatus).conditions ?? [];
  const s = surface as { status?: string };
  if (!s.status || s.status === 'healthy') return [];
  return [s.status as ToothStatusType];
}

/** Serialize ToothData surfaces + root_zones into the JSONB format for the DB */
export function serializeToothSurfaces(
  surfaces: ToothData['surfaces'],
  rootZones?: RootZoneStatus[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(surfaces)) {
    if (!val) continue;
    if ('conditions' in val) {
      result[key] = val;
    } else {
      const old = val as { status?: string; material?: string };
      result[key] = {
        conditions: old.status && old.status !== 'healthy' ? [old.status] : [],
        material: old.material,
      };
    }
  }
  if (rootZones && rootZones.length > 0) {
    result['_root_zones'] = rootZones;
  }
  return result;
}

/** Parse root_zones from the JSONB surfaces field */
export function parseRootZones(surfaces: Record<string, unknown>): RootZoneStatus[] {
  const raw = surfaces['_root_zones'];
  if (!Array.isArray(raw)) return [];
  return raw as RootZoneStatus[];
}

const CONDITION_SEVERITY: Record<ToothStatusType, number> = {
  extraction_planned: 9,
  missing: 8,
  periapical_abscess: 7,
  fracture: 6,
  caries: 5,
  root_canal: 4,
  post_and_core: 3,
  filled: 3,
  crown: 2,
  implant: 2,
  bridge: 2,
  veneer: 1,
  healthy: 0,
};

export function getPrimaryStatus(status: ToothStatusType[]): ToothStatusType {
  if (!status || status.length === 0) return 'healthy';
  return status.reduce((a, b) =>
    (CONDITION_SEVERITY[a] ?? 0) >= (CONDITION_SEVERITY[b] ?? 0) ? a : b,
  );
}

export function getDbStatus(status: ToothStatusType[]): string {
  const validOnly = status.filter((s) => DB_VALID_STATUSES.includes(s));
  if (validOnly.length === 0) return 'healthy';
  return getPrimaryStatus(validOnly);
}
